import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useEditEventNotification } from "../../hooks/useEditEventNotification";
import EventNotificationEmailEditor from "../EventNotificationEmailEditor/EventNotificationEmailEditor";
import EmailTemplateVariableSelector from "@/modules/emailManagement/features/EmailTemplateManagement/components/EmailTemplateVariableSelector/EmailTemplateVariableSelector";

const EditEventNotification: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const {
    form,
    submitForm,
    fetchNotification,
    handleCancel,
    notification,
    isLoading,
  } = useEditEventNotification();

  const { control, handleSubmit, formState, watch, getValues, setValue } = form;
  const { isSubmitting } = formState;

  const isMailChannelEnabled = watch("isMailChannelEnabled");
  const isSmsChannelEnabled = watch("isSmsChannelEnabled");

  useEffect(() => {
    if (id) {
      fetchNotification(Number(id));
    }
  }, [id, fetchNotification]);

  const handleInsertVariableToSmsContent = (variableName: string) => {
    const textarea = document.querySelector(
      "textarea[name='smsContent']",
    ) as HTMLTextAreaElement;
    if (!textarea) return;

    const currentContent = getValues("smsContent");
    const { selectionStart, selectionEnd } = textarea;
    const newContent =
      currentContent.slice(0, selectionStart) +
      variableName +
      currentContent.slice(selectionEnd);

    setValue("smsContent", newContent);

    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd =
        selectionStart + variableName.length;
      textarea.focus();
    });
  };

  return (
    <MainPageWrapper loading={isLoading} title="">
      <div>
        <Form {...form}>
          <form className="space-y-6 pb-24" onSubmit={handleSubmit(submitForm)}>
            <fieldset>
              <div className="mb-6">
                <h2 className="text-xl font-medium text-gray-900 dark:text-white">
                  Edit Notification: {notification?.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Configure the notification channels and templates
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Email Settings
                </h3>

                <FormField
                  control={control}
                  name="isMailChannelEnabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">
                          Email Channel
                        </FormLabel>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Enable or disable email notifications
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {isMailChannelEnabled && (
                  <EventNotificationEmailEditor form={form} />
                )}
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  SMS Settings
                </h3>

                <FormField
                  control={control}
                  name="isSmsChannelEnabled"
                  render={({ field }) => (
                    <FormItem className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-slate-700 p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">SMS Channel</FormLabel>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          Enable or disable SMS notifications
                        </p>
                      </div>
                      <FormControl>
                        <Switch
                          checked={Boolean(field.value)}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {isSmsChannelEnabled && (
                  <FormField
                    control={control}
                    name="smsContent"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex justify-between items-center">
                          <FormLabel>SMS Content</FormLabel>
                          <EmailTemplateVariableSelector
                            onVariableSelect={handleInsertVariableToSmsContent}
                          />
                        </div>
                        <FormControl>
                          <Textarea
                            {...field}
                            className="w-full min-h-[100px] h-auto"
                            placeholder="Enter SMS message content..."
                          />
                        </FormControl>
                        <p className="text-sm text-gray-500 mt-1">
                          Plain text message for SMS notifications
                        </p>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className="sticky bottom-0 flex justify-end gap-4 px-4 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-950 mt-8">
                <Button onClick={handleCancel} type="button" variant="outline">
                  Cancel
                </Button>
                <Button disabled={isLoading} type="submit">
                  {isSubmitting || isLoading
                    ? "Saving..."
                    : "Save Notification"}
                </Button>
              </div>
            </fieldset>
          </form>
        </Form>
      </div>
    </MainPageWrapper>
  );
};

export default EditEventNotification;
